import { useRef } from 'react';
import { useGroupLicensePermissions } from '@group/License';
import { useHandler } from '@framework/hooks';
import { SVGToImage } from '@model-framework/image/SVGToImage';
import type { RenderedImage } from '@model-framework/image/SVGToImage';
import { ViewCaptureTargetLocator } from '@view-model/ui/components/View/ViewCaptureTargetLocator';
import type { ViewCollectionContents } from '@view-model/domain/view/ViewCollectionContents';
import { CAPTURE_IMAGE_MAX_BASE64_LENGTH, pngDataUrlToBase64 } from './CaptureImageResult';
import type { CaptureImageResult } from './CaptureImageResult';
import type { CaptureTargetView, CaptureViewTarget } from './CaptureViewTarget';
import type { ViewImageCaptureAPI, ViewImageCaptureResult } from './ViewImageCaptureLocator';
import {
  CaptureBusyError,
  CaptureNotReadyError,
  CapturePayloadTooLargeError,
  CapturePermissionDeniedError,
  CaptureViewNameAmbiguousError,
  CaptureViewNotFoundError,
} from './ViewImageCaptureError';
import { waitForSVGGeometryStable } from './waitForSVGGeometryStable';

// 出力画像の長辺上限（px）。AIチャット添付画像の圧縮実装（compressChatImage.ts）と同値で、
// Claude 系 API が内部リサイズする上限に合わせた値
const CAPTURE_IMAGE_MAX_DIMENSION = 1568;

type Args = {
  viewModelSVGRef: React.RefObject<SVGSVGElement | null>;
  viewContents: ViewCollectionContents | undefined;
  deselectAllViews: () => void;
  zoomToFit: () => void;
};

// エラー detail や全体キャプチャの成功 result に同梱する現存ビュー一覧。
// meaningPanel はキャプチャ対象として案内しない内部的なビューのため original のみを列挙する。
function listCaptureTargetViews(viewContents: ViewCollectionContents): CaptureTargetView[] {
  return viewContents
    .getViews()
    .filterByViewType('original')
    .toArray()
    .map((view) => ({ id: view.id, name: view.name.value }));
}

// キャプチャ対象の viewId を解決する。
// viewId 指定時の存在判定はドメインデータ（viewContents）で行う。レジストリ（マウント済み View）
// だけで判定すると、ロード直後など未マウントの間に「not found なのに existing views に同じ id が
// 載る」という自己矛盾したエラーになり、エージェントが同じ失敗を繰り返すため。
// viewName 指定時は trim 後の完全一致で照合する。照合対象はエラー detail の一覧・案内対象と
// 整合させるため original ビューのみとする（decisions.md §2）。
function resolveCaptureTargetViewId(viewContents: ViewCollectionContents, target: CaptureViewTarget): string {
  if ('viewId' in target) {
    if (!viewContents.findView(target.viewId)) {
      throw new CaptureViewNotFoundError(target.viewId, listCaptureTargetViews(viewContents));
    }
    return target.viewId;
  }

  const viewName = target.viewName.trim();
  const candidates = viewContents
    .getViews()
    .filterByViewType('original')
    .toArray()
    .filter((view) => view.name.value.trim() === viewName);
  if (candidates.length === 0) {
    throw new CaptureViewNotFoundError(target.viewName, listCaptureTargetViews(viewContents));
  }
  if (candidates.length > 1) {
    throw new CaptureViewNameAmbiguousError(
      target.viewName,
      candidates.map((view) => ({ id: view.id, name: view.name.value }))
    );
  }
  return candidates[0].id;
}

// 画像化結果をワイヤ仕様の CaptureImageResult へ変換する。
// MCPサーバ側の WebSocket 受信上限を超えて接続ごと切断されるのを防ぐため、送信前にサイズを検査する。
function buildCaptureImageResult(rendered: RenderedImage): CaptureImageResult {
  const dataBase64 = pngDataUrlToBase64(rendered.dataUrl);
  if (dataBase64.length > CAPTURE_IMAGE_MAX_BASE64_LENGTH) {
    throw new CapturePayloadTooLargeError(dataBase64.length);
  }
  return {
    completedAt: new Date().toISOString(),
    image: { mimeType: 'image/png', dataBase64 },
    width: rendered.width,
    height: rendered.height,
  };
}

// 画像キャプチャ操作（ビューモデル全体・ビュー単体）を提供する。
// MCPブリッジ経由の JSON-RPC ハンドラと、AIアシスタントの getViewImage ツールの双方から使う。
// 失敗は ViewImageCaptureError の型付きエラーとして throw し、呼び出し側がそれぞれの経路の
// 失敗表現へ変換する（requestModeling が typed error を throw する構造と揃えている）。
export const useViewImageCapture = ({
  viewModelSVGRef,
  viewContents,
  deselectAllViews,
  zoomToFit,
}: Args): ViewImageCaptureAPI => {
  const permissions = useGroupLicensePermissions();
  // キャプチャの同時実行ガード。2つのキャプチャ操作の間でも共有し、実行中の再入は capture_busy で拒否する
  const capturingRef = useRef(false);

  // 権限チェックと同時実行ガードを共通化する。
  // 権限は手動エクスポート UI（HeaderMenuLeft）と同じ imageExport ライセンス権限を参照する。
  const runExclusively = async <T>(run: () => Promise<T>): Promise<T> => {
    if (!permissions?.imageExport) {
      throw new CapturePermissionDeniedError();
    }
    if (capturingRef.current) {
      throw new CaptureBusyError();
    }
    capturingRef.current = true;
    try {
      return await run();
    } finally {
      capturingRef.current = false;
    }
  };

  const captureViewModelImage = useHandler(async (): Promise<CaptureImageResult> => {
    return runExclusively(async () => {
      const svg = viewModelSVGRef.current;
      if (!svg) {
        throw new CaptureNotReadyError();
      }

      // 手動エクスポート（全体）と同じ前処理: 選択解除 + zoom-to-fit。
      // 手動フローと異なり、MCP 経路ではズーム・レイアウトの安定を待ってからキャプチャする
      deselectAllViews();
      zoomToFit();
      await waitForSVGGeometryStable(svg);

      const rendered = await SVGToImage.render(svg, { maxDimension: CAPTURE_IMAGE_MAX_DIMENSION });
      const result = buildCaptureImageResult(rendered);
      // 現存ビュー一覧を同梱し、エージェントが失敗を経由せず次の get_view_image で正しい
      // id/名前を指定できるようにする（decisions.md §2）。viewContents 未取得時は
      // 空配列で「ビューが無い」と誤認させないためフィールドごと省略する
      if (viewContents === undefined) {
        return result;
      }
      return { ...result, views: listCaptureTargetViews(viewContents) };
    });
  });

  const captureViewImage = useHandler(async (captureTarget: CaptureViewTarget): Promise<ViewImageCaptureResult> => {
    return runExclusively(async () => {
      if (!viewContents) {
        throw new CaptureNotReadyError();
      }

      const viewId = resolveCaptureTargetViewId(viewContents, captureTarget);

      const target = ViewCaptureTargetLocator.find(viewId);
      const gView = target?.getView() ?? null;
      const gViewTitle = target?.getViewTitle() ?? null;
      const viewRect = viewContents.getRectOf(viewId);
      if (!gView || !gViewTitle || !viewRect) {
        // ドメイン上は存在するが View コンポーネント未マウント等で描画要素・矩形が未解決。
        // 再試行で回復し得るため not_ready（open_target_and_retry）として返す
        throw new CaptureNotReadyError();
      }

      const rendered = await SVGToImage.renderFromViewParts(gView, gViewTitle, viewRect, {
        maxDimension: CAPTURE_IMAGE_MAX_DIMENSION,
      });
      const view = viewContents.findView(viewId);
      return {
        view: { id: viewId, name: view?.name.value ?? '' },
        image: buildCaptureImageResult(rendered),
      };
    });
  });

  return { captureViewModelImage, captureViewImage };
};
